import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import {
  fetchEmployee,
  fetchEmployeeEmployment,
  useEmployee,
} from '~/data/employee';
import { useEmployeeEmploymentData } from '~/data/employee/useEmployeeEmploymentData';
import { fetchEmployeeDetails } from '~/data/employeeProfile';
import { fetchEmployees, useAllEmployees } from '~/data/employees';
import { useEmploymentDetails } from '~/data/employment/useEmploymentDetails';
import { useAllEnums } from '~/data/enums';
import { useOrgTree } from '~/data/orgunits';
import { useMySpecificUnits } from '~/data/orgunits/useMySpecificUnits';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
} from '~/data/proposals';
import {
  filterAndSortEmploymentProposals,
  useEmploymentProposals,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { useEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmployment';
import {
  getContracts,
  mapDefaultData,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/data';
import {
  Contracts,
  PendingScheduleChanges,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useSchedulePendingEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useSchedulePendingEmployment';
import { useSWR } from '~/hooks/useSWR';
import { useResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalTrackingListResultDto } from '~/services/Http/client.iam.generated';
import { isRoleHR, isRoleManager } from '~/services/Permissions';
import { AppDispatch } from '~/store';
import { browserLogError } from '~/utils/helper';

type PropsType = {
  employeeId: number;
};

export const useScheduleChangesData = (props: PropsType) => {
  const { employeeId } = props;

  const dispatch: AppDispatch = useDispatch();

  const { orgTree } = useOrgTree();
  const { allEnums } = useAllEnums();

  // General configs & data
  const { applicationConfig } = useApplicationConfig();
  const managerCanTerminate =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrTerminateEmployee;
  const managerCanRehire =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrRehireEmployee;

  // Logged user
  const loggedInUserId = useLoggedInEmployeeId();
  const roles = useLoggedInUserRoles();
  const isHRUser = isRoleHR(roles);
  const isMgrOrAltMgr = isRoleManager(roles);

  // Responsible HRs
  const responsibleHRSelect = useResponsibleHRSelect(employeeId);
  const {
    allResponsibleHRIds,
    defaultResponsibleHrId,
    isLoading: HRRespLoading,
  } = responsibleHRSelect;

  // Employee & employment data
  const {
    employee: { empBasicData, empPosition, fEmpBusinessUnits },
  } = useEmployee();
  const { getEmployeeById, isLoading: employeesLoading } = useAllEmployees();
  const { employeeInfo, fEmpEmplId } = useMemo(() => {
    const employeeInfo = getEmployeeById(employeeId);

    return {
      employeeInfo,
      fEmpEmplId: employeeInfo?.fEmpEmplId ?? -1,
    };
  }, [getEmployeeById, employeeId]);

  const { data: empContracts, mutate: mutateContracts } = useSWR<{
    contracts: Contracts[];
    profile: { firstName: string; lastName: string };
  }>(
    ['contracts', empBasicData.fEmpProfileId],
    () => getContracts(empBasicData.fEmpProfileId),
    { revalidateOnFocus: false },
  );
  const { mapEmploymentPostData } = useEmployment();
  const { data: employmentsDetail } = useEmploymentDetails(
    employeeId,
    employeeInfo?.fEmpEmplId,
  );

  const {
    data: pendingData,
    isLoading: isLoadingPendingData,
    mutateArray: mutatePendingData,
  } = useSchedulePendingEmployment(employeeId, { revalidateOnFocus: false });

  const { employeeEmploymentData } = useEmployeeEmploymentData();
  const employmentData = useMemo(
    () => employeeEmploymentData[0],
    [employeeEmploymentData],
  );
  const hasEmploymentStarted = useMemo(() => {
    return moment(employmentData.fEmplDateStart, 'YYYY-MM-DD').isSameOrBefore(
      new Date(),
      'date',
    );
  }, [employmentData]);

  // Proposals
  const proposalsData = useEmploymentProposals({
    employeeId,
  });

  const {
    details: {
      proposals: defaultDetailsProposals,
      isLoading: detailsPropLoading,
      mutate: mutateDetailsPos,
    },
    positions: {
      proposals: defaultEmpPositionProposals,
      isLoading: empPosPropsLoading,
      mutate: mutateInternalPos,
    },
    config: { proposalsEnabled },
  } = proposalsData;

  const employmentDetailsProposals = filterAndSortEmploymentProposals(
    defaultDetailsProposals,
  );

  const empPositionProposals = filterAndSortEmploymentProposals(
    defaultEmpPositionProposals,
  );

  const { aMyHRUnits, aMyManUnits, aMyAltUnits } = useMySpecificUnits();
  const restrictField = useMemo(() => {
    const isInMyUnits = (units: number[]) => {
      return !!employeeInfo ? units.includes(employeeInfo.fEmpUnit) : false;
    };

    const inMyHRUnit = isInMyUnits(aMyHRUnits);
    const inMyMgrUnit = isInMyUnits(aMyManUnits);
    const inMyAltMgrUnit = isInMyUnits(aMyAltUnits);
    const myOverrideManagerEmployee =
      employeeInfo?.fEmpManagerId === loggedInUserId;

    const hrResponsibleOverEmployee = isHRUser && inMyHRUnit;
    const managerResponsibleOverEmployee =
      isMgrOrAltMgr &&
      (inMyMgrUnit || inMyAltMgrUnit || myOverrideManagerEmployee);

    const canTerminate = {
      asHR: hrResponsibleOverEmployee,
      asManager: managerCanTerminate && managerResponsibleOverEmployee,
    };
    const canRehire = {
      asHR: hrResponsibleOverEmployee,
      asManager: managerCanRehire && managerResponsibleOverEmployee,
    };

    const notAllowEmpTermDate = !(canTerminate.asHR || canTerminate.asManager);
    const managerCantRehire = !(canRehire.asHR || canRehire.asManager);

    return {
      manager: isMgrOrAltMgr,
      hr: isHRUser,
      inMyHRUnit: inMyHRUnit,
      notAllowEmpTermDate,
      managerCantRehire,
    };
  }, [
    isMgrOrAltMgr,
    isHRUser,
    aMyHRUnits,
    aMyManUnits,
    aMyAltUnits,
    employeeInfo,
    managerCanRehire,
    managerCanTerminate,
    loggedInUserId,
  ]);

  const getInitialFieldValues = useCallback(
    ({
      emplDetPropData,
      newPendingData,
      empPositionPropData,
    }: {
      empPositionPropData?: EmploymentPositionProposalProps;
      emplDetPropData?: EmploymentDetailsProposalProps;
      newPendingData?: PendingScheduleChanges[];
    }) => {
      const hasEmploymentDetails = employmentsDetail.length > 0;

      if (!hasEmploymentDetails) {
        browserLogError(
          `Scheduled Employment Changes: Employee doesn't have Employment Details`,
        );

        return [];
      }

      return mapDefaultData({
        empCurrentData: empPosition,
        employmentCurrentData: employmentData,
        employmentCurrentDetail: employmentsDetail[0],
        fEmpBusinessUnits,
        orgTree,
        allEnums,
        contractNumber: empBasicData.fEmpContractNumber,
        personNumber: empBasicData.fEmpPersonNumber,
        pendingData: newPendingData ? newPendingData : pendingData!,
        mainContract:
          empContracts?.contracts.find((c) => c.employeeId === employeeId)
            ?.isDefault || false,
        employmentDetailsProposal: proposalsEnabled
          ? emplDetPropData || employmentDetailsProposals[0]
          : undefined,
        empPositionProposal: proposalsEnabled
          ? empPositionPropData || empPositionProposals[0]
          : undefined,
        defaultResponsibleHrId:
          isHRUser && allResponsibleHRIds.includes(loggedInUserId)
            ? loggedInUserId
            : defaultResponsibleHrId,
      });
    },
    [
      allEnums,
      allResponsibleHRIds,
      defaultResponsibleHrId,
      empBasicData.fEmpContractNumber,
      empBasicData.fEmpPersonNumber,
      empContracts?.contracts,
      employeeId,
      empPosition,
      empPositionProposals,
      employmentData,
      employmentDetailsProposals,
      employmentsDetail,
      fEmpBusinessUnits,
      isHRUser,
      loggedInUserId,
      proposalsEnabled,
      orgTree,
      pendingData,
    ],
  );

  // Loading state
  const isLoadingProposals = proposalsEnabled
    ? empPosPropsLoading || detailsPropLoading
    : false;
  const isLoadingData =
    employeesLoading ||
    isLoadingProposals ||
    isLoadingPendingData ||
    HRRespLoading;

  const refreshAllEmployeeData = useCallback(() => {
    return Promise.all([
      dispatch(fetchEmployees({ myRoles: roles })),
      dispatch(fetchEmployee({ empId: `${employeeId}` })),
      dispatch(fetchEmployeeEmployment({ empId: `${employeeId}` })),
      dispatch(fetchEmployeeDetails(`${employeeId}`)),
      mutatePendingData(),
      mutateContracts(),
      mutateDetailsPos(),
      mutateInternalPos(),
    ]).then(
      ([
        _allEmployees,
        _employee,
        _employeeEmployment,
        _employeeDetails,
        pendingData,
        _contracts,
        detailsPosition,
        internalPosition,
      ]): {
        pendingData: PendingScheduleChanges[];
        detailsPosition: ProposalTrackingListResultDto | undefined;
        internalPosition: ProposalTrackingListResultDto | undefined;
      } => {
        return {
          pendingData,
          detailsPosition,
          internalPosition,
        };
      },
    );
  }, [
    roles,
    employeeId,
    mutatePendingData,
    mutateContracts,
    mutateDetailsPos,
    mutateInternalPos,
    dispatch,
  ]);

  return {
    isLoading: isLoadingData,
    access: {
      roles,
      restrictField,
    },
    proposalsData,
    employee: {
      empBasicData,
      fEmpEmplId, // Investigate use & need
      empContracts,
      mapEmploymentPostData,
      employmentsDetail,
    },
    pendingData,
    responsibleHRSelect,
    mutateData: {
      mutatePendingData,
      mutateContracts,
      mutateDetailsPos,
      mutateInternalPos,
    },
    getInitialFieldValues,
    refreshAllEmployeeData,
    hasEmploymentStarted,
  };
};
